import React, { useState, useEffect } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import axios from '../../axios/axiosconf';
import './SuperadminStyles.css';

const ClienteForm = () => {
    const history = useHistory();
    const { id } = useParams();
    const isEditMode = !!id;
    const [formData, setFormData] = useState({
        nombre: '',
        nit: '',
        razon_social: '',
        direccion: '',
        telefono: '',
        email_contacto: '',
        limite_usuarios: '',
        estado: 1,
        tipo_contrato: 'mensual',
        fecha_vencimiento: ''
    });
    const [originalLimite, setOriginalLimite] = useState(0);
    const [usuariosActuales, setUsuariosActuales] = useState(0);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(isEditMode);

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        if (isEditMode) {
            fetchCliente();
        }
    }, [history, id, isEditMode]);

    const fetchCliente = async () => {
        try {
            const response = await axios.get(`/superadmin/clientes/${id}`);
            const cliente = response.data;
            setFormData({
                nombre: cliente.nombre || '',
                nit: cliente.nit || '',
                razon_social: cliente.razon_social || '',
                direccion: cliente.direccion || '',
                telefono: cliente.telefono || '',
                email_contacto: cliente.email_contacto || '',
                limite_usuarios: cliente.limite_usuarios || '',
                estado: cliente.estado,
                tipo_contrato: cliente.tipo_contrato || 'mensual',
                fecha_vencimiento: cliente.fecha_vencimiento || ''
            });
            setOriginalLimite(cliente.limite_usuarios);
            setUsuariosActuales(cliente.usuarios_actuales || 0);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching cliente:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            } else {
                setErrors({ general: 'Error al cargar el cliente' });
                setLoading(false);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'number' ? parseInt(value) || '' : value
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre es requerido';
        }

        if (!formData.nit.trim()) {
            newErrors.nit = 'El NIT es requerido';
        }

        if (!formData.email_contacto.trim()) {
            newErrors.email_contacto = 'El email de contacto es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_contacto)) {
            newErrors.email_contacto = 'El email no tiene un formato válido';
        }

        if (!formData.limite_usuarios || formData.limite_usuarios < 1) {
            newErrors.limite_usuarios = 'El límite de usuarios debe ser al menos 1';
        }

        if (isEditMode && formData.limite_usuarios < usuariosActuales) {
            newErrors.limite_usuarios = `No puede reducir el límite por debajo de los usuarios actuales (${usuariosActuales})`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            if (isEditMode) {
                await axios.put(`/superadmin/clientes/${id}`, formData);
            } else {
                await axios.post('/superadmin/clientes', formData);
            }
            history.push('/superadmin/clientes');
        } catch (error) {
            console.error('Error saving cliente:', error);
            if (error.response && error.response.data.errors) {
                setErrors(error.response.data.errors);
            } else {
                setErrors({
                    general: error.response?.data?.message || `Error al ${isEditMode ? 'actualizar' : 'crear'} el cliente`
                });
            }
            setSubmitting(false);
        }
    };

    const showLimiteWarning = () => {
        return isEditMode && 
               formData.limite_usuarios && 
               formData.limite_usuarios < originalLimite && 
               formData.limite_usuarios >= usuariosActuales;
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>
                    <i className="fas fa-building"></i> {isEditMode ? 'Editar Cliente' : 'Nuevo Cliente'}
                </h4>
                <Link to="/superadmin/clientes" className="btn btn-secondary">
                    <i className="fas fa-arrow-left"></i> Volver
                </Link>
            </div>

            {errors.general && (
                <div className="alert alert-danger">
                    {errors.general}
                </div>
            )}

            {showLimiteWarning() && (
                <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle"></i> Está reduciendo el límite de usuarios.
                    Actualmente hay {usuariosActuales} usuario(s) activo(s).
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <h5>Información Básica</h5>
                    
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="nombre">Nombre <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                                    id="nombre"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    placeholder="Nombre del cliente"
                                />
                                {errors.nombre && (
                                    <div className="invalid-feedback">{errors.nombre}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="nit">NIT/Documento <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.nit ? 'is-invalid' : ''}`}
                                    id="nit"
                                    name="nit"
                                    value={formData.nit}
                                    onChange={handleChange}
                                    placeholder="NIT o documento de identificación"
                                />
                                {errors.nit && (
                                    <div className="invalid-feedback">{errors.nit}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="razon_social">Razón Social</label>
                        <input
                            type="text"
                            className="form-control"
                            id="razon_social"
                            name="razon_social"
                            value={formData.razon_social}
                            onChange={handleChange}
                            placeholder="Razón social del cliente"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="direccion">Dirección</label>
                        <input
                            type="text"
                            className="form-control"
                            id="direccion"
                            name="direccion"
                            value={formData.direccion}
                            onChange={handleChange}
                            placeholder="Dirección física"
                        />
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="telefono">Teléfono</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="telefono"
                                    name="telefono"
                                    value={formData.telefono}
                                    onChange={handleChange}
                                    placeholder="Teléfono de contacto"
                                />
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="email_contacto">Email de Contacto <span className="text-danger">*</span></label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email_contacto ? 'is-invalid' : ''}`}
                                    id="email_contacto"
                                    name="email_contacto"
                                    value={formData.email_contacto}
                                    onChange={handleChange}
                                    placeholder="email@ejemplo.com"
                                />
                                {errors.email_contacto && (
                                    <div className="invalid-feedback">{errors.email_contacto}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h5>Configuración</h5>
                    
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="limite_usuarios">Límite de Usuarios <span className="text-danger">*</span></label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.limite_usuarios ? 'is-invalid' : ''}`}
                                    id="limite_usuarios"
                                    name="limite_usuarios"
                                    value={formData.limite_usuarios}
                                    onChange={handleChange}
                                    min="1"
                                    placeholder="Número máximo de usuarios"
                                />
                                {errors.limite_usuarios && (
                                    <div className="invalid-feedback">{errors.limite_usuarios}</div>
                                )}
                                {isEditMode && (
                                    <small className="form-text text-muted">
                                        Usuarios actuales: {usuariosActuales}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="estado">Estado</label>
                                <select
                                    className="form-control"
                                    id="estado"
                                    name="estado"
                                    value={formData.estado}
                                    onChange={handleChange}
                                >
                                    <option value={0}>Activo</option>
                                    <option value={1}>Pendiente Por Pagar</option>
                                    <option value={2}>Bloqueado</option>
                                </select>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="fecha_vencimiento">Fecha de Vencimiento</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="fecha_vencimiento"
                                    name="fecha_vencimiento"
                                    value={formData.fecha_vencimiento}
                                    onChange={handleChange}
                                />
                                <small className="form-text text-muted">Opcional</small>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="tipo_contrato">Tipo de Contrato</label>
                                <select
                                    className="form-control"
                                    id="tipo_contrato"
                                    name="tipo_contrato"
                                    value={formData.tipo_contrato}
                                    onChange={handleChange}
                                >
                                    <option value="mensual">Mensual</option>
                                    <option value="trimestral">Trimestral</option>
                                    <option value="semestral">Semestral</option>
                                    <option value="anual">Anual</option>
                                </select>
                                <small className="form-text text-muted">Útil para reportes junto con fecha de vencimiento</small>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-group">
                    <button
                        type="submit"
                        className="btn btn-superadmin"
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i> {isEditMode ? 'Actualizar Cliente' : 'Crear Cliente'}
                            </>
                        )}
                    </button>
                    <Link to="/superadmin/clientes" className="btn btn-secondary ml-2">
                        Cancelar
                    </Link>
                </div>
            </form>
        </SuperadminLayout>
    );
};

export default ClienteForm;
